import React, { useState, useEffect, useCallback } from 'react';
import {
  Row,
  Col,
  Form,
  Button,
  Card,
  CardBody,
  CardHeader,
  Alert,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  FormGroup,
} from 'reactstrap';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import DocumentUploadZone from './Components/DocumentUploadZone';
import DocumentList from './Components/DocumentList';
import PartnerDocumentCard from './Components/PartnerDocumentCard';
import RequiredDocumentsChecklist from './Components/RequiredDocumentsChecklist';
import DocumentPreviewPanel from './Components/DocumentPreviewPanel';
import { documentsService } from '../../../services/documentsService';
import {
  DocumentOwnerType,
  CompanyDocumentType,
  PartnerDocumentType,
  IndividualDocumentType,
  CompanyDocumentDto,
  PartnerDocumentDto,
  IndividualDocumentDto,
  GetDocumentsByCompanyResult,
  GetDocumentsByPartnerResult,
} from '../../../types/documents';

interface Phase3DocumentsFormProps {
  onSubmit: (values: any) => void;
  onPrevious: () => void;
  initialValues?: any;
  isSubmitting: boolean;
  companyId: string;
  partners: any[];
  isFinalPhase?: boolean;
  registrationType?: 'abertura' | 'portabilidade';
}

const Phase3DocumentsForm: React.FC<Phase3DocumentsFormProps> = ({
  onSubmit,
  onPrevious,
  initialValues,
  isSubmitting,
  companyId,
  partners,
  isFinalPhase = false,
  registrationType = 'portabilidade',
}) => {
  const { t } = useTranslation();

  // Default to 'partners' tab for 'abertura' (no CNPJ), 'company' tab for 'portabilidade' (has CNPJ)
  const [activeTab, setActiveTab] = useState(registrationType === 'portabilidade' ? 'company' : 'partners');
  const [loading, setLoading] = useState(false);
  const [showUploadZone, setShowUploadZone] = useState(false);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto | null>(null);
  const [isPreviewExpanded, setIsPreviewExpanded] = useState(false);
  const [filterDocumentType, setFilterDocumentType] = useState<CompanyDocumentType | PartnerDocumentType | IndividualDocumentType | null>(null);

  // Company Documents State
  const [companyDocumentsData, setCompanyDocumentsData] =
    useState<GetDocumentsByCompanyResult | null>(null);

  // Partner Documents State
  const [selectedPartner, setSelectedPartner] = useState<string | null>(null);
  const [partnerDocumentsData, setPartnerDocumentsData] =
    useState<GetDocumentsByPartnerResult | null>(null);
  const [partnerDocumentsList, setPartnerDocumentsList] = useState<
    Record<string, GetDocumentsByPartnerResult>
  >({});

  // Replace Document Modal
  const [replaceModal, setReplaceModal] = useState<{
    isOpen: boolean;
    document: CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto | null;
  }>({ isOpen: false, document: null });
  const [replaceFile, setReplaceFile] = useState<File | null>(null);
  const [replaceProgress, setReplaceProgress] = useState(0);
  const [isReplacing, setIsReplacing] = useState(false);
  const [estimatedTime, setEstimatedTime] = useState(24);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Processing document tracking
  const [processingDocumentId, setProcessingDocumentId] = useState<string | null>(null);

  // Track initial load to prevent duplicate calls
  const initialLoadRef = React.useRef<{
    companyId: string | null;
    activeTab: string | null;
    selectedPartner: string | null;
    includeDeleted: boolean;
  }>({
    companyId: null,
    activeTab: null,
    selectedPartner: null,
    includeDeleted: false,
  });

  // Track if we're currently loading all partners to prevent race conditions
  const isLoadingAllPartnersRef = React.useRef(false);

  // Load company documents
  const loadCompanyDocuments = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const data = await documentsService.getCompanyDocuments(
        companyId,
        undefined,
        undefined,
        includeDeleted
      );
      setCompanyDocumentsData(data);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || t('Erro ao carregar documentos da empresa')
      );
    } finally {
      if (!silent) setLoading(false);
    }
  }, [companyId, includeDeleted, t]);

  // Load partner documents
  const loadPartnerDocuments = useCallback(
    async (partnerId: string, silent = false) => {
      try {
        if (!silent) setLoading(true);
        const data = await documentsService.getPartnerDocuments(
          partnerId,
          undefined,
          undefined,
          includeDeleted
        );
        setPartnerDocumentsData(data);
        setPartnerDocumentsList((prev) => ({
          ...prev,
          [partnerId]: data,
        }));
      } catch (error: any) {
        toast.error(
          error.response?.data?.message || t('Erro ao carregar documentos do sócio')
        );
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [includeDeleted, t]
  );

  // Load all partner documents summaries
  const loadAllPartnerDocuments = useCallback(async () => {
    try {
      setLoading(true);
      isLoadingAllPartnersRef.current = true; // Mark as loading
      const results: Record<string, GetDocumentsByPartnerResult> = {};

      for (const partner of partners) {
        try {
          const data = await documentsService.getPartnerDocuments(
            partner.id,
            undefined,
            undefined,
            false // Don't include deleted for summary
          );
          results[partner.id] = data;
        } catch (error) {
          console.error(`Error loading documents for partner ${partner.id}`, error);
        }
      }

      setPartnerDocumentsList(results);
    } catch (error: any) {
      toast.error(t('Erro ao carregar documentos dos sócios'));
    } finally {
      isLoadingAllPartnersRef.current = false; // Mark as done
      setLoading(false);
    }
  }, [partners, t]);

  // Consolidated effect to load documents (prevents duplicate calls)
  useEffect(() => {
    // Only load documents if companyId exists (not during new company creation)
    if (!companyId) return;

    const isInitialLoad =
      initialLoadRef.current.companyId !== companyId ||
      initialLoadRef.current.activeTab !== activeTab;

    const hasPartnerChanged = initialLoadRef.current.selectedPartner !== selectedPartner;
    const hasIncludeDeletedChanged = initialLoadRef.current.includeDeleted !== includeDeleted;

    // Update ref to track current state AFTER checking changes
    initialLoadRef.current = { companyId, activeTab, selectedPartner, includeDeleted };

    if (activeTab === 'company') {
      loadCompanyDocuments();
    } else if (activeTab === 'partners') {
      // Load all partner documents summary on initial load
      if (isInitialLoad) {
        loadAllPartnerDocuments();
      }
      // Handle selected partner document loading
      if (selectedPartner) {
        // Don't make individual calls if we're currently loading all partners
        if (isLoadingAllPartnersRef.current) {
          // Wait for the bulk load to finish, data will be set when it completes
          return;
        }

        const cachedData = partnerDocumentsList[selectedPartner];

        // Determine if we need to fetch data or use cached data
        if (hasPartnerChanged || hasIncludeDeletedChanged) {
          // Partner or filter changed
          if (!cachedData || hasIncludeDeletedChanged) {
            // No cached data OR filter changed - need to fetch
            loadPartnerDocuments(selectedPartner);
          } else {
            // Have cached data - use it
            setPartnerDocumentsData(cachedData);
          }
        } else if (cachedData) {
          // Partner hasn't changed, but we have cached data available (e.g., from bulk load)
          // Set it to display (React will bail out if it's the same object)
          setPartnerDocumentsData(cachedData);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, companyId, includeDeleted, selectedPartner, partnerDocumentsList]);

  // Handle company document upload
  const handleCompanyDocumentUpload = async (
    file: File,
    documentType: CompanyDocumentType | PartnerDocumentType | IndividualDocumentType,
    onProgress: (progress: number) => void
  ) => {
    await documentsService.uploadCompanyDocument({
      companyId,
      file,
      documentType: documentType as CompanyDocumentType,
      onProgress,
    });
    await loadCompanyDocuments(true); // Silent refresh without loading spinner
  };

  // Handle partner document upload
  const handlePartnerDocumentUpload = async (
    file: File,
    documentType: CompanyDocumentType | PartnerDocumentType | IndividualDocumentType,
    onProgress: (progress: number) => void
  ) => {
    if (!selectedPartner) return;

    await documentsService.uploadPartnerDocument({
      partnerId: selectedPartner,
      companyId,
      file,
      documentType: documentType as PartnerDocumentType,
      onProgress,
    });
    await loadPartnerDocuments(selectedPartner, true); // Silent refresh without loading spinner
    await loadAllPartnerDocuments(); // Refresh summary
  };

  // Handle document download
  const handleDocumentDownload = (
    document: CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto
  ) => {
    if (document.signedUrl) {
      documentsService.downloadDocument(document.signedUrl, document.originalFileName);
    } else {
      toast.error(t('URL de download não disponível'));
    }
  };

  // Handle document preview
  const handleDocumentPreview = (
    document: CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto
  ) => {
    if (document.signedUrl) {
      documentsService.previewDocument(document.signedUrl);
    } else {
      toast.error(t('URL de visualização não disponível'));
    }
  };

  // Timer effect for replace operation
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isReplacing && elapsedTime < estimatedTime) {
      interval = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isReplacing, elapsedTime, estimatedTime]);

  // Handle replace document
  const handleReplaceClick = (document: CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto) => {
    setReplaceModal({ isOpen: true, document });
    setReplaceFile(null);
    setReplaceProgress(0);
    setEstimatedTime(24);
    setElapsedTime(0);
  };

  const handleReplaceConfirm = async () => {
    if (!replaceModal.document || !replaceFile) return;

    try {
      setIsReplacing(true);
      setElapsedTime(0);

      const ownerType = 'companyId' in replaceModal.document && !('partnerId' in replaceModal.document)
        ? DocumentOwnerType.Company
        : DocumentOwnerType.Partner;

      await documentsService.replaceDocument({
        documentId: replaceModal.document.id,
        ownerType,
        file: replaceFile,
        onProgress: (progress) => setReplaceProgress(progress),
      });

      toast.success(t('Documento substituído com sucesso'));
      setReplaceModal({ isOpen: false, document: null });
      setReplaceFile(null);
      setReplaceProgress(0);
      setElapsedTime(0);

      // Reload documents (silent refresh - no loading spinner)
      if (activeTab === 'company') {
        await loadCompanyDocuments(true);
      } else if (selectedPartner) {
        await loadPartnerDocuments(selectedPartner, true);
        await loadAllPartnerDocuments();
      }
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || t('Erro ao substituir documento')
      );
    } finally {
      setIsReplacing(false);
      setElapsedTime(0);
    }
  };

  // Handle delete document
  const handleDocumentDelete = async (
    document: CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto,
    reason: string
  ) => {
    try {
      setProcessingDocumentId(document.id);
      const ownerType = 'companyId' in document && !('partnerId' in document)
        ? DocumentOwnerType.Company
        : DocumentOwnerType.Partner;

      await documentsService.deleteDocument({
        documentId: document.id,
        ownerType,
        deleteReason: reason,
      });

      toast.success(t('Documento excluído com sucesso'));

      // Reload documents (silent refresh - no loading spinner)
      if (activeTab === 'company') {
        await loadCompanyDocuments(true);
      } else if (selectedPartner) {
        await loadPartnerDocuments(selectedPartner, true);
        await loadAllPartnerDocuments();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('Erro ao excluir documento'));
    } finally {
      setProcessingDocumentId(null);
    }
  };

  // Handle restore document
  const handleDocumentRestore = async (
    document: CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto,
    reason: string
  ) => {
    try {
      setProcessingDocumentId(document.id);
      const ownerType = 'companyId' in document && !('partnerId' in document)
        ? DocumentOwnerType.Company
        : DocumentOwnerType.Partner;

      await documentsService.restoreDocument({
        documentId: document.id,
        ownerType,
        restoreReason: reason,
      });

      toast.success(t('Documento restaurado com sucesso'));

      // Reload documents (silent refresh - no loading spinner)
      if (activeTab === 'company') {
        await loadCompanyDocuments(true);
      } else if (selectedPartner) {
        await loadPartnerDocuments(selectedPartner, true);
        await loadAllPartnerDocuments();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('Erro ao restaurar documento'));
    } finally {
      setProcessingDocumentId(null);
    }
  };

  // Handle document type change (reclassify document)
  const handleDocumentTypeChange = async (
    document: CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto,
    newType: CompanyDocumentType | PartnerDocumentType | IndividualDocumentType
  ) => {
    try {
      setProcessingDocumentId(document.id);
      const ownerType = 'companyId' in document && !('partnerId' in document)
        ? DocumentOwnerType.Company
        : DocumentOwnerType.Partner;

      await documentsService.updateDocumentType({
        documentId: document.id,
        ownerType,
        newDocumentType: newType,
      });

      toast.success(t('Tipo de documento atualizado com sucesso'));

      // Reload documents
      if (activeTab === 'company') {
        await loadCompanyDocuments(true); // Silent refresh
      } else if (selectedPartner) {
        await loadPartnerDocuments(selectedPartner, true); // Silent refresh
        await loadAllPartnerDocuments();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('Erro ao atualizar tipo de documento'));
    } finally {
      setProcessingDocumentId(null);
    }
  };

  // Handle filename update
  const handleFileNameUpdate = async (
    document: CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto,
    newFileName: string
  ) => {
    try {
      setProcessingDocumentId(document.id);
      const ownerType = 'companyId' in document && !('partnerId' in document)
        ? DocumentOwnerType.Company
        : DocumentOwnerType.Partner;

      await documentsService.updateDocumentFileName({
        documentId: document.id,
        ownerType,
        newFileName,
      });

      toast.success(t('Nome do arquivo atualizado com sucesso'));

      // Reload documents
      if (activeTab === 'company') {
        await loadCompanyDocuments(true); // Silent refresh
      } else if (selectedPartner) {
        await loadPartnerDocuments(selectedPartner, true); // Silent refresh
        await loadAllPartnerDocuments();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('Erro ao atualizar nome do arquivo'));
    } finally {
      setProcessingDocumentId(null);
    }
  };

  // Handle partner selection for document management
  const handleManagePartnerDocuments = (partnerId: string) => {
    setSelectedPartner(partnerId);
    setShowUploadZone(true);
    // Note: loadPartnerDocuments will be called automatically by useEffect when selectedPartner changes
  };

  // Handle back from partner documents
  const handleBackToPartnerList = () => {
    setSelectedPartner(null);
    setShowUploadZone(false);
    setPartnerDocumentsData(null);
  };

  // Handle finish
  const handleFinish = () => {
    onSubmit({});
  };

  return (
    <Form>
      <Row>
        <Col lg={12}>
          <Alert color="info" className="mb-4">
            <i className="ri-information-line me-2"></i>
            {t(
              isFinalPhase
                ? 'Fase 3: Gestão de documentos da empresa e dos sócios. Faça upload de documentos, gerencie versões e acompanhe expirações.'
                : 'Fase 2: Gestão de documentos da empresa e dos sócios. Faça upload de documentos, gerencie versões e acompanhe expirações.'
            )}
          </Alert>
        </Col>
      </Row>

      {/* Tab Navigation */}
      <Row>
        <Col lg={12}>
          <Card className="mb-4">
            <CardBody>
              <Nav tabs className="nav-tabs-custom">
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === 'company' })}
                    onClick={() => {
                      setActiveTab('company');
                      setSelectedPartner(null);
                      setShowUploadZone(false);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <i className="ri-building-line me-2"></i>
                    {t('Documentos da Empresa')}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === 'partners' })}
                    onClick={() => {
                      setActiveTab('partners');
                      setShowUploadZone(false);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <i className="ri-team-line me-2"></i>
                    {t('Documentos dos Sócios')}
                  </NavLink>
                </NavItem>
              </Nav>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Tab Content */}
      <TabContent activeTab={activeTab}>
        {/* Company Documents Tab */}
        <TabPane tabId="company">
          {loading && (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="text-muted mt-2">{t('Carregando documentos...')}</p>
            </div>
          )}

          {!loading && companyDocumentsData && (
            <>
              {/* Upload Zone Toggle */}
              <Row className="mb-3">
                <Col>
                  <Button
                    color="primary"
                    onClick={() => setShowUploadZone(!showUploadZone)}
                  >
                    <i
                      className={`ri-${
                        showUploadZone ? 'arrow-up' : 'upload'
                      }-line me-1`}
                    ></i>
                    {showUploadZone ? t('Ocultar Upload') : t('Upload de Documentos')}
                  </Button>
                  <Button
                    color="light"
                    className="ms-2"
                    onClick={() => setIncludeDeleted(!includeDeleted)}
                  >
                    <i className="ri-eye-line me-1"></i>
                    {includeDeleted
                      ? t('Ocultar Excluídos')
                      : t('Mostrar Excluídos')}
                  </Button>
                  {filterDocumentType !== null && (
                    <Button
                      color="secondary"
                      outline
                      className="ms-2"
                      onClick={() => setFilterDocumentType(null)}
                    >
                      <i className="ri-close-line me-1"></i>
                      {t('Limpar Filtro')}
                    </Button>
                  )}
                </Col>
              </Row>

              {/* Upload Zone */}
              {showUploadZone && (
                <DocumentUploadZone
                  ownerType={DocumentOwnerType.Company}
                  ownerId={companyId}
                  companyId={companyId}
                  onUpload={handleCompanyDocumentUpload}
                  onUploadComplete={() => {
                    setShowUploadZone(false);
                    loadCompanyDocuments(true); // Silent refresh without loading spinner
                  }}
                />
              )}

              {/* Main Content with 2-column layout */}
              <Row>
                {/* Left Column: Required Documents + Document List */}
                {!isPreviewExpanded && (
                  <Col xxl={selectedDocument ? 9 : 12} lg={selectedDocument ? 8 : 12}>
                    {/* Required Documents Checklist */}
                    <div className="mb-3">
                      <RequiredDocumentsChecklist
                        ownerType={DocumentOwnerType.Company}
                        documents={companyDocumentsData.documents}
                        onDocumentClick={(docType) => {
                          setFilterDocumentType(docType);
                          setSelectedDocument(null);
                        }}
                      />
                    </div>

                    {/* Document List */}
                    <div>
                      <DocumentList
                        ownerType={DocumentOwnerType.Company}
                        documents={
                          filterDocumentType !== null
                            ? companyDocumentsData.documents.filter(
                                (doc) => doc.documentType === filterDocumentType
                              )
                            : companyDocumentsData.documents
                        }
                        onDownload={handleDocumentDownload}
                        onPreview={handleDocumentPreview}
                        onDocumentSelect={setSelectedDocument}
                        onReplace={handleReplaceClick}
                        onDelete={handleDocumentDelete}
                        onRestore={handleDocumentRestore}
                        onDocumentTypeChange={handleDocumentTypeChange}
                        onFileNameUpdate={handleFileNameUpdate}
                        includeDeleted={includeDeleted}
                        isAdmin={true}
                        processingDocumentId={processingDocumentId}
                      />
                    </div>
                  </Col>
                )}

                {/* Right Column: Document Preview Panel (Fixed or Expanded) */}
                {selectedDocument && (
                  <Col xxl={isPreviewExpanded ? 12 : 3} lg={isPreviewExpanded ? 12 : 4} className="mb-3">
                    <div style={{ position: 'sticky', top: '20px' }}>
                      <DocumentPreviewPanel
                        document={selectedDocument}
                        ownerType={DocumentOwnerType.Company}
                        isExpanded={isPreviewExpanded}
                        onClose={() => {
                          setSelectedDocument(null);
                          setIsPreviewExpanded(false);
                        }}
                        onToggleExpand={() => setIsPreviewExpanded(!isPreviewExpanded)}
                        onDownload={() => handleDocumentDownload(selectedDocument)}
                        onReplace={() => handleReplaceClick(selectedDocument)}
                        onDelete={() => {
                          const reason = prompt(t('Motivo da exclusão:'));
                          if (reason) {
                            handleDocumentDelete(selectedDocument, reason);
                            setSelectedDocument(null);
                            setIsPreviewExpanded(false);
                          }
                        }}
                        isAdmin={true}
                      />
                    </div>
                  </Col>
                )}
              </Row>
            </>
          )}
        </TabPane>

        {/* Partner Documents Tab */}
        <TabPane tabId="partners">
          {!selectedPartner && (
            <Row>
              <Col lg={12} className="mb-3">
                <h5>{t('Selecione um sócio para gerenciar documentos')}</h5>
              </Col>
              {partners.map((partner) => {
                const partnerData = partnerDocumentsList[partner.id];
                return (
                  <Col key={partner.id} lg={6} xl={4} className="mb-3">
                    <PartnerDocumentCard
                      partner={partner}
                      statistics={
                        partnerData?.statistics || {
                          totalDocuments: 0,
                          pendingDocuments: 0,
                          validDocuments: 0,
                          expiredDocuments: 0,
                          expiringSoonDocuments: 0,
                          deletedDocuments: 0,
                        }
                      }
                      completeness={
                        partnerData?.completeness || {
                          hasIdentityDocument: false,
                          hasCPF: false,
                          hasProofOfAddress: false,
                          hasECPF: false,
                          isComplete: false,
                        }
                      }
                      onManageDocuments={handleManagePartnerDocuments}
                    />
                  </Col>
                );
              })}
            </Row>
          )}

          {selectedPartner && partnerDocumentsData && (
            <>
              {/* Back Button */}
              <Row className="mb-3">
                <Col>
                  <Button color="light" onClick={handleBackToPartnerList}>
                    <i className="ri-arrow-left-line me-1"></i>
                    {t('Voltar para Lista de Sócios')}
                  </Button>
                </Col>
              </Row>

              {/* Partner Info */}
              <Card className="border mb-3">
                <CardHeader>
                  <h5 className="card-title mb-0">
                    <i className="ri-user-line me-2"></i>
                    {partners.find((p) => p.id === selectedPartner)?.name}
                  </h5>
                </CardHeader>
                <CardBody>
                  <p className="text-muted mb-0">
                    CPF: {partners.find((p) => p.id === selectedPartner)?.cpf}
                  </p>
                </CardBody>
              </Card>

              {/* Upload Zone Toggle */}
              <Row className="mb-3">
                <Col>
                  <Button
                    color="primary"
                    onClick={() => setShowUploadZone(!showUploadZone)}
                  >
                    <i
                      className={`ri-${
                        showUploadZone ? 'arrow-up' : 'upload'
                      }-line me-1`}
                    ></i>
                    {showUploadZone ? t('Ocultar Upload') : t('Upload de Documentos')}
                  </Button>
                  <Button
                    color="light"
                    className="ms-2"
                    onClick={() => setIncludeDeleted(!includeDeleted)}
                  >
                    <i className="ri-eye-line me-1"></i>
                    {includeDeleted
                      ? t('Ocultar Excluídos')
                      : t('Mostrar Excluídos')}
                  </Button>
                  {filterDocumentType !== null && (
                    <Button
                      color="secondary"
                      outline
                      className="ms-2"
                      onClick={() => setFilterDocumentType(null)}
                    >
                      <i className="ri-close-line me-1"></i>
                      {t('Limpar Filtro')}
                    </Button>
                  )}
                </Col>
              </Row>

              {/* Upload Zone */}
              {showUploadZone && (
                <DocumentUploadZone
                  ownerType={DocumentOwnerType.Partner}
                  ownerId={selectedPartner}
                  companyId={companyId}
                  onUpload={handlePartnerDocumentUpload}
                  onUploadComplete={() => {
                    setShowUploadZone(false);
                    if (selectedPartner) {
                      loadPartnerDocuments(selectedPartner, true); // Silent refresh without loading spinner
                      // Update the cached partner in partnerDocumentsList
                      // No need to reload all partners - only this one changed
                    }
                  }}
                />
              )}

              {/* Main Content with 2-column layout */}
              <Row>
                {/* Left Column: Required Documents + Document List */}
                {!isPreviewExpanded && (
                  <Col xxl={selectedDocument ? 9 : 12} lg={selectedDocument ? 8 : 12}>
                    {/* Required Documents Checklist */}
                    <div className="mb-3">
                      <RequiredDocumentsChecklist
                        ownerType={DocumentOwnerType.Partner}
                        documents={partnerDocumentsData.documents}
                        onDocumentClick={(docType) => {
                          setFilterDocumentType(docType);
                          setSelectedDocument(null);
                        }}
                      />
                    </div>

                    {/* Document List */}
                    <div>
                      <DocumentList
                        ownerType={DocumentOwnerType.Partner}
                        documents={
                          filterDocumentType !== null
                            ? partnerDocumentsData.documents.filter(
                                (doc) => doc.documentType === filterDocumentType
                              )
                            : partnerDocumentsData.documents
                        }
                        onDownload={handleDocumentDownload}
                        onPreview={handleDocumentPreview}
                        onDocumentSelect={setSelectedDocument}
                        onReplace={handleReplaceClick}
                        onDelete={handleDocumentDelete}
                        onRestore={handleDocumentRestore}
                        onDocumentTypeChange={handleDocumentTypeChange}
                        onFileNameUpdate={handleFileNameUpdate}
                        includeDeleted={includeDeleted}
                        isAdmin={true}
                        processingDocumentId={processingDocumentId}
                      />
                    </div>
                  </Col>
                )}

                {/* Right Column: Document Preview Panel (Fixed or Expanded) */}
                {selectedDocument && (
                  <Col xxl={isPreviewExpanded ? 12 : 3} lg={isPreviewExpanded ? 12 : 4} className="mb-3">
                    <div style={{ position: 'sticky', top: '20px' }}>
                      <DocumentPreviewPanel
                        document={selectedDocument}
                        ownerType={DocumentOwnerType.Partner}
                        isExpanded={isPreviewExpanded}
                        onClose={() => {
                          setSelectedDocument(null);
                          setIsPreviewExpanded(false);
                        }}
                        onToggleExpand={() => setIsPreviewExpanded(!isPreviewExpanded)}
                        onDownload={() => handleDocumentDownload(selectedDocument)}
                        onReplace={() => handleReplaceClick(selectedDocument)}
                        onDelete={() => {
                          const reason = prompt(t('Motivo da exclusão:'));
                          if (reason) {
                            handleDocumentDelete(selectedDocument, reason);
                            setSelectedDocument(null);
                            setIsPreviewExpanded(false);
                          }
                        }}
                        isAdmin={true}
                      />
                    </div>
                  </Col>
                )}
              </Row>
            </>
          )}
        </TabPane>
      </TabContent>

      {/* Form Actions */}
      <div className="d-flex justify-content-between gap-2 mt-4">
        <Button color="secondary" outline onClick={onPrevious}>
          <i className="ri-arrow-left-line me-1"></i>
          {t('Anterior')}
        </Button>

        <Button
          type="button"
          color="success"
          disabled={isSubmitting}
          onClick={handleFinish}
        >
          {isSubmitting ? (
            <>
              <i className="ri-loader-4-line rotating me-1"></i>
              {isFinalPhase ? t('Finalizando...') : t('Salvando...')}
            </>
          ) : (
            <>
              {isFinalPhase ? (
                <>
                  <i className="ri-check-line me-1"></i>
                  {t('Finalizar Cadastro')}
                </>
              ) : (
                <>
                  {t('Próximo')}
                  <i className="ri-arrow-right-line ms-1"></i>
                </>
              )}
            </>
          )}
        </Button>
      </div>

      {/* Replace Document Modal */}
      <Modal
        isOpen={replaceModal.isOpen}
        toggle={() => setReplaceModal({ isOpen: false, document: null })}
      >
        <ModalHeader
          toggle={() => setReplaceModal({ isOpen: false, document: null })}
        >
          {t('Substituir Documento')}
        </ModalHeader>
        <ModalBody>
          <p>
            {t('Substituir o documento')}{' '}
            <strong>{replaceModal.document?.originalFileName}</strong>
          </p>
          <FormGroup>
            <Label for="replaceFile">{t('Selecione o novo arquivo')}</Label>
            <Input
              type="file"
              id="replaceFile"
              onChange={(e) => setReplaceFile(e.target.files?.[0] || null)}
            />
          </FormGroup>
          {isReplacing && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted">
                  {t('Substituindo documento...')} {replaceProgress}%
                </span>
                <span className="badge bg-info">
                  <i className="ri-time-line me-1"></i>
                  {elapsedTime}s / ~{estimatedTime}s
                </span>
              </div>
              <div className="progress mb-3" style={{ height: '25px' }}>
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={{ width: `${replaceProgress}%` }}
                >
                  {replaceProgress}%
                </div>
              </div>
              <Alert color="info" className="mb-0">
                <i className="ri-information-line me-2"></i>
                <small>
                  {t('Esta operação pode levar até 24 segundos para mover o arquivo no armazenamento R2.')}
                </small>
              </Alert>
            </div>
          )}
          {!isReplacing && (
            <Alert color="info" className="mb-0">
              <i className="ri-time-line me-2"></i>
              <small>
                {t('Tempo estimado: ~24 segundos (movimentação de arquivo no armazenamento)')}
              </small>
            </Alert>
          )}
          <Alert color="warning" className="mt-3">
            <i className="ri-alert-line me-2"></i>
            {t(
              'O arquivo anterior será movido para a pasta de exclusão e a versão será incrementada.'
            )}
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setReplaceModal({ isOpen: false, document: null })}
            disabled={isReplacing}
          >
            {t('Cancelar')}
          </Button>
          <Button
            color="primary"
            onClick={handleReplaceConfirm}
            disabled={!replaceFile || isReplacing}
          >
            <i className="ri-refresh-line me-1"></i>
            {t('Substituir')}
          </Button>
        </ModalFooter>
      </Modal>
    </Form>
  );
};

export default Phase3DocumentsForm;
